'use strict';

const { gql } = require('graphql-tag');

var _parent = {};

var accessorials = {
  init: p => (_parent = p),

  // -------------------------------- CONSTANTS -------------------------------- //

  DEFAULT_DURATION_SECONDS: 300,
  DEFAULT_PRICE_PER_MINUTE: 0.33,
  DEFAULT_PAY_PER_MINUTE: 0.17,

  // -------------------------------- ENUMS -------------------------------- //

  /**
   * Types psuedo enum
   *
   * Value and code are the same here as there's no numeric value of a type
   */
  costBearers: Object.freeze({
    COMPANY: {
      value: 'company',
      name: 'Company',
      code: 'company',
      desc: 'When the driver issued company credit card is used in making the purchase and thus no reimbursement is necessary',
    },
    DRIVER: {
      value: 'driver',
      name: 'Driver',
      code: 'driver',
      desc: 'When the driver is the one making the purchase with their own personal funds and thus needing reimbursement',
    },
  }),

  /**
   * Status codes psuedo enum
   *
   * Value prop intended to serve as a poor mans sequence indication here.
   */
  statusCodes: Object.freeze({
    PENDING: {
      value: 0,
      name: 'Pending',
      code: 'pending',
      desc: 'Accessorial that is not finalized yet and should be excluded from AR or AP calcs.',
    },
    APPROVED: {
      value: 100,
      name: 'Approved',
      code: 'approved',
      desc: 'Accessorial that has been charged or manually approved and should be included in AR and AP calcs.',
    },
    DECLINED: {
      value: 200,
      name: 'Declined',
      code: 'declined',
      desc: 'Accessorial with credit card authorization which has been declined at least once and is no longer valid to charge the credit card. It also should not be included in any AR or AP calcs because it was declined too many times and never actually charged.',
    },
    EXPIRED: {
      value: 300,
      name: 'Expired',
      code: 'expired',
      desc: 'Accessorial with credit card authorization which is no longer valid to charge the credit card. It also should not be included in any AR or AP calcs because it expired before it was used.',
    },
  }),

  /**
   * Types psuedo enum
   *
   * Value and code are the same here as there's no numeric value of a type
   */
  types: Object.freeze({
    DELAY: {
      value: 'delay',
      name: 'Delay',
      code: 'delay',
      desc: 'Charge incurred for an idle driver based on time spent.',
    },
    FUEL: {
      value: 'fuel',
      name: 'Fuel',
      code: 'fuel',
      desc: 'Charge to cover the cost of refueling the vehicle.',
    },
    INSURANCE: {
      value: 'insurance',
      name: 'Insurance',
      code: 'insurance',
      desc: 'Charge incurred when out of region insurance is used.',
    },
    PARTIAL: {
      value: 'partial',
      name: 'Partial',
      code: 'partial',
      desc: 'Charge for a partially completed move.',
    },
    RIDE: {
      value: 'ride',
      name: 'Ride',
      code: 'ride',
      desc: 'Charge to cover the cost of a ride for a stranded driver.',
    },
    ROADSIDE: {
      value: 'roadside',
      name: 'Roadside',
      code: 'roadside',
      desc: 'Charge to cover the cost of roadside assistance for the vehicle.',
    },
    TOLL: {
      value: 'toll',
      name: 'Toll',
      code: 'toll',
      desc: 'Charge to cover the cost of a toll during the move.',
    },
    TOW: {
      value: 'tow',
      name: 'Tow',
      code: 'tow',
      desc: 'Charge to cover the cost of towing the vehicle.',
    },
    WAIVED: {
      value: 'waived',
      name: 'Waived',
      code: 'waived',
      desc: 'Flag to waive all move charges.',
    },
    REIMBURSEMENT: {
      value: 'reimbursement',
      name: 'Reimbursement',
      code: 'reimbursement',
      desc: 'Reimbursement for a driver expense.',
    },
    SURGE: {
      value: 'surge',
      name: 'Surge',
      code: 'surge',
      desc: 'Charge to cover the cost of a surge during the move.',
    },
  }),

  // -------------------------------- GQL FUNCTIONS -------------------------------- //

  /**
   * Create one or more new accessorials in the database
   *
   * @param  {array} accessorials - An array of accessorial record objects
   * @returns {object} A graphql mutation response
   */
  create: async objs => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_accessorials_create($objectArray: [accessorials_insert_input!]!) {
          insert_accessorials(objects: $objectArray) {
            affected_rows
          }
        }
      `,
      {
        objectArray: objs,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Update an accessorial record in the database
   *
   * @param  {number} id - An id for the accessorial record to update
   * @param  {array} accessorial - A partial accessorial object with updated values
   * @returns {object} A graphql mutation response
   */
  update: async (id, obj) => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_accessorials_update($id: bigint!, $accessorial: accessorials_set_input!) {
          update_accessorials(where: { id: { _eq: $id } }, _set: $accessorial) {
            affected_rows
          }
        }
      `,
      {
        id: id,
        accessorial: obj,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  updateRideAccessorial: async (id, obj) => {
    const res = await _parent.gql.mutation(
      gql`
        mutation updateAccessorial($id: bigint!, $input: accessorials_set_input!) {
          update_accessorials(where: { id: { _eq: $id } }, _set: $input) {
            affected_rows
            returning {
              id
              cost
            }
          }
        }
      `,
      {
        id: id,
        input: {
          ...obj,
          updatedat: 'now()',
        },
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  updateSurge: async (id, obj) => {
    const res = await _parent.gql.mutation(
      gql`
        mutation updateAccessorial($moveId: bigint!, $input: accessorials_set_input!) {
          update_accessorials(where: { move_id: { _eq: $moveId }, code: { _eq: "surge" } }, _set: $input) {
            affected_rows
          }
        }
      `,
      {
        moveId: id,
        input: {
          ...obj,
          updatedat: 'now()',
        },
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  // -------------------------------- HELPER FUNCTIONS -------------------------------- //

  /**
   * Check against the list of types to ensure a type is valid
   *
   * @param  {string} type - A type code to check against the valid list of type codes
   * @returns {bool} True if the type is valid
   */
  isValidType: type => {
    return _parent.accessorials.types.map(t => t.code).includes(type);
  },

  /** Calculates the delay price based on the amount of idle minutes and price per minute */
  calcDelayPrice: (driverIdleTimeMinutes, customerPricePerMinute) => {
    let minutes = driverIdleTimeMinutes || 0;
    let payPerMinute = customerPricePerMinute || _parent.accessorials.DEFAULT_PRICE_PER_MINUTE;
    return minutes * payPerMinute;
  },

  /** Calculates the delay pay based on the amount of idle minutes and pay per minute */
  calcDelayPay: (driverIdleTimeMinutes, driverPayPerMinute) => {
    let minutes = driverIdleTimeMinutes || 0;
    let payPerMinute = driverPayPerMinute || _parent.accessorials.DEFAULT_PAY_PER_MINUTE;
    return minutes * payPerMinute;
  },

  /** Takes's a move status calculates and returns a partial price based on that status */
  calcPartialPrice: (moveStatus = null, moveCost = 0) => {
    let partialPrice = 0;
    switch (moveStatus) {
      case 'pickup started':
        partialPrice = moveCost * 0.25;
        break;
      case 'pickup arrived':
        partialPrice = moveCost * 0.4;
        break;
      case 'pickup successful':
        partialPrice = moveCost * 0.5;
        break;
      case 'delivery started':
        partialPrice = moveCost * 0.75;
        break;
      case 'delivery arrived':
        partialPrice = moveCost * 0.9;
        break;
      case 'delivery successful':
        partialPrice = moveCost * 1;
        break;
      default:
        partialPrice = moveCost * 0.1;
        break;
    }
    return partialPrice;
  },

  /** Takes's a move status calculates and returns a partial pay based on that status */
  calcPartialPay: (moveStatus = null, movePay = 0) => {
    let partialPay = 0;
    switch (moveStatus) {
      case 'pickup started':
        partialPay = movePay * 0.25;
        break;
      case 'pickup arrived':
        partialPay = movePay * 0.4;
        break;
      case 'pickup successful':
        partialPay = movePay * 0.5;
        break;
      case 'delivery started':
        partialPay = movePay * 0.75;
        break;
      case 'delivery arrived':
        partialPay = movePay * 0.9;
        break;
      case 'delivery successful':
        partialPay = movePay * 1;
        break;
      default:
        partialPay = movePay * 0.1;
        break;
    }
    return partialPay;
  },

  /** Merge two accessorials together, using the primary as the base + adding the secondary cost, ar_amount & ap_amount */
  mergeAccessorials: (primaryAccessorial = {}, secondaryAccessorial = {}) => {
    // Validation
    if (!primaryAccessorial) throw new Error('Invalid primary accessorial!');
    if (!secondaryAccessorial) throw new Error('Invalid secondary accessorial!');

    // Merge accessorial
    const mergedAccessorial = {
      ...primaryAccessorial,
      cost: _parent.utilities.roundToNearestPenny(primaryAccessorial.cost + secondaryAccessorial.cost),
      ar_amount: _parent.utilities.roundToNearestPenny(primaryAccessorial.ar_amount + secondaryAccessorial.ar_amount),
      ap_amount: _parent.utilities.roundToNearestPenny(primaryAccessorial.ap_amount + secondaryAccessorial.ap_amount),
    };

    // Return accessorial
    return mergedAccessorial;
  },

  // -------------------------------- BUILD FUNCTIONS -------------------------------- //

  /** Build generalized accessorial object (using db schema). Reused in specific accessorial building for consistency.
   * @param {number} options.moveId - ID of the move the accessorial is for
   * @param {string} options.code - Type of accessorial to be created
   * @param {string} options.status - (Optional) Status of accessorial (pending, approved, declined, expired). Deafaults to approved.
   * @param {number} options.cost - (Optional) Cost amount to the HopDrive
   * @param {number} options.ar_amount - (Optional) Charge amount to the customer
   * @param {number} options.ap_amount - (Optional) Pay amount to the driver
   * @param {string} options.notes - (Optional) Additional notes on the accessorial (Customer & Driver facing)
   * @returns {object} - An insertable accessorial object
   */
  buildAccessorial: (
    options = {
      moveId: null,
      code: null,
      status: _parent.accessorials.statusCodes.APPROVED.code,
      cost: 0,
      ar_amount: 0,
      ap_amount: 0,
      notes: null,
    }
  ) => {
    try {
      // Validation for required props
      if (!options || typeof options !== `object`)
        throw new Error(`Failed to build accessorial: Invalid options prop!`);
      if (!options.moveId || typeof options.moveId !== `number`)
        throw new Error(`Failed to build accessorial: Invalid moveId prop!`);
      if (!options.code || typeof options.code !== `string`)
        throw new Error(`Failed to build accessorial: Invalid code prop!`);

      // Validation for optional props
      if (options.cost && typeof options.cost !== `number`)
        throw new Error(`Failed to build accessorial: Invalid cost prop!`);
      if (options.ar_amount && typeof options.ar_amount !== `number`)
        throw new Error(`Failed to build accessorial: Invalid ar_amount prop!`);
      if (options.ap_amount && typeof options.ap_amount !== `number`)
        throw new Error(`Failed to build accessorial: Invalid ap_amount prop!`);
      if (options.notes && typeof options.notes !== `string`)
        throw new Error(`Failed to build accessorial: Invalid notes prop!`);
      if (options.status && typeof options.status !== `string`)
        throw new Error(`Failed to build accessorial: Invalid status prop!`);
      // Status codes need an additional check so that only valid status codes are used
      const validCodes = Object.values(_parent.accessorials.statusCodes).map(statusCode => statusCode.code);
      if (options.status && !validCodes.includes(options.status))
        throw new Error(`Failed to build accessorial: Status prop ${options.status} is not a valid status!`);

      // Round all numbers to 2 decimal places to avoid floating point errors
      // NOTE: The roundToNearestPenny function from utilities always rounds up, even if the amount is only 1 billionth of a penny over.
      options.cost = options.cost ? Number(options.cost.toFixed(2)) : 0;
      options.ar_amount = options.ar_amount ? Number(options.ar_amount.toFixed(2)) : 0;
      options.ap_amount = options.ap_amount ? Number(options.ap_amount.toFixed(2)) : 0;
      options.notes = options.notes || null;

      // Build accessorial
      const newAccessorial = {
        move_id: options.moveId,
        code: options.code,
        status: options.status,
        cost: options.cost,
        ar_amount: options.ar_amount,
        ap_amount: options.ap_amount,
        notes: options.notes,
      };

      // Return accessorial
      return newAccessorial;
    } catch (err) {
      console.error(err);
      return null;
    }
  },

  /** Build Delay Accessorial
   *
   ** HopDrive Responsibility - There is never any direct cost to HopDrive.
   ** Customer Responsibility - If the customer is responsible, they must cover driver idle time.
   ** Driver Responsibility - The driver must be paid for their idle time.
   *
   ** Cost to HopDrive - Always 0.
   ** Charge to Customer - Driver idle time, multiplied by the provided customer price rate.
   ** Pay to Driver - Driver idle time, multiplied by the provided driver pay rate.
   *
   * @param {number} options.moveId - ID of the move the accessorial is for
   * @param {boolean} options.customerResponsible - Determines if the customer should be charged
   * @param {number} options.driverIdleTimeMinutes - Amount of time the driver was idle (in minutes)
   * @param {number} options.customerPricePerMinute - (Optional) Price per minute rate to determine customer price
   * @param {number} options.driverPayPerMinute - (Optional) Pay per minute rate to determine driver pay
   * @param {string} options.notes - (Optional) Additional notes on the accessorial (Customer & Driver facing)
   * @returns {object} - An insertable accessorial object
   */
  buildDelayAccessorial: (
    options = {
      moveId: null,
      customerResponsible: true,
      driverIdleTimeMinutes: 0,
      customerPricePerMinute: 0,
      driverPayPerMinute: 0,
      notes: null,
    }
  ) => {
    try {
      // Validation for required props
      if (!options || typeof options !== `object`)
        throw new Error(`Failed to build delay accessorial: Invalid options prop!`);
      if (!options.moveId || typeof options.moveId !== `number`)
        throw new Error(`Failed to build delay accessorial: Invalid moveId prop!`);
      if (typeof options.customerResponsible !== `boolean`)
        throw new Error(`Failed to build delay accessorial: Invalid customerResponsible prop!`);
      if (typeof options.driverIdleTimeMinutes !== `number`)
        throw new Error(`Failed to build delay accessorial: Invalid driverIdleTimeMinutes prop!`);
      if (typeof options.customerPricePerMinute !== `number`)
        throw new Error(`Failed to build delay accessorial: Invalid customerPricePerMinute prop!`);
      if (typeof options.driverPayPerMinute !== `number`)
        throw new Error(`Failed to build delay accessorial: Invalid driverPayPerMinute prop!`);

      // Validation for optional props
      if (options.notes && typeof options.notes !== `string`)
        throw new Error(`Failed to build delay accessorial: Invalid notes prop!`);

      // Calculate delay price to customer (if customer is responsible)
      const delayPrice = options.customerResponsible
        ? _parent.accessorials.calcDelayPrice(options.driverIdleTimeMinutes, options.customerPricePerMinute)
        : 0;

      // Calculate delay pay to driver
      const delayPay = _parent.accessorials.calcDelayPay(options.driverIdleTimeMinutes, options.driverPayPerMinute);

      // Build accessorial
      const newAccessorial = _parent.accessorials.buildAccessorial({
        moveId: options.moveId,
        code: _parent.accessorials.types.DELAY.code,
        cost: 0,
        ar_amount: delayPrice,
        ap_amount: delayPay,
        notes: options.notes || _parent.accessorials.types.DELAY.desc,
      });

      // Return accessorial
      return newAccessorial;
    } catch (err) {
      console.error('Error building Delay Accessorial', err);
      return null;
    }
  },

  /** Build Fuel Accessorial
   *
   * This function takes the aproach of assuming the caller wants defaults when
   * they pass incorrect information rather than throwing a bunch of errors.
   * Check that the object you got back is what you expected before using it.
   * You've been warned!
   *
   * @param {object} options
   * @param {number} options.moveId The id of the move the accessorial is for
   * @param {number} options.cost The amount actually charged at the time the card is swiped (after initial hold was released for fuel pumps)
   * @param {number} options.fuelMultiplier The factor to use when calculating the fuel charge to add to the customers invoice for the move. Typically 1.5 times the cost.
   * @param {boolean} options.calcDriverTimePay If true, use the payPerMinute and durationSeconds options to calculate the driver pay
   * @param {number} options.payPerMinute The dollars per minute to use as a factor when calculating the driver time based reimbursement.
   * @param {number} options.durationSeconds The number of seconds a refuel took (or is expected to take) to use in the driver time based pay calculation
   * @param {string} options.notes Default notes for the accessorial
   * @param {string} options.status Status of the accessorial. Defaults to 'pending'. Fuel accessorials need to be approved unless they are directly created in Admin Portal.
   * @returns {object} A fuel accessorial object
   */
  buildFuelAccessorial: (
    options = {
      moveId: null,
      cost: 0,
      fuelMultiplier: _parent.accessorials.DEFAULT_UPCHARGE_MULTIPLIER, //This should be passed in from a market config
      calcDriverTimePay: false,
      payPerMinute: _parent.accessorials.DEFAULT_PAY_PER_MINUTE, //This should be passed in from a market config
      durationSeconds: _parent.accessorials.DEFAULT_DURATION_SECONDS, //This should be passed in from a market config
      status: _parent.accessorials.statusCodes.PENDING.code,
      notes: '',
    }
  ) => {
    // TODO: The defaults sprinkled throughout this code need to come from an outside
    // configuration of some kind or need to always be passed in as required params

    //Auto generated note
    let note = 'Generated';

    //Check if we got a move id to associate this with. If not use null.
    const moveId = options.moveId || null;

    //Parse the options and validate expected values before we continue
    const fuelCost = isNaN(options.cost) ? 0 : Number(options.cost);
    const fuelMultiplier = isNaN(options.fuelMultiplier) ? 1.5 : Number(options.fuelMultiplier);
    const customerFuelCharge = _parent.utilities.roundToNearestPenny(fuelCost * fuelMultiplier);
    note += ` | fuel multiplier: ${fuelMultiplier}`;

    //Initialize the driver reimbursement amount to 0
    let driverReimbursementAmount = 0;

    //Increase the driver pay to include time based pay
    const calcDriverTimePay = typeof options.calcDriverTimePay !== 'boolean' ? false : options.calcDriverTimePay;
    const payPerMinute = isNaN(options.payPerMinute)
      ? _parent.accessorials.DEFAULT_UPCHARGE_MULTIPLIER
      : Number(options.payPerMinute);
    const durationSeconds = isNaN(options.durationSeconds)
      ? _parent.accessorials.DEFAULT_DURATION_SECONDS
      : Number(options.durationSeconds);
    if (calcDriverTimePay) {
      const timePay = _parent.utilities.roundToNearestPenny((payPerMinute * durationSeconds) / 60);
      note += ` | driver time pay: ${timePay}`;
      driverReimbursementAmount += timePay;
    }

    let ret = {
      move_id: moveId,
      code: _parent.accessorials.types.FUEL.code,
      status: options.status,
      cost: fuelCost,
      ar_amount: customerFuelCharge,
      ap_amount: driverReimbursementAmount,
      notes: note,
    };

    //Conditionally add other optional props if passed
    if (options.id) ret.id = options.id;
    if (options.notes) ret.notes = options.notes;

    return ret;
  },

  /** Build Partial Accessorial
   *
   ** HopDrive Responsibility - There is never any direct cost to HopDrive.
   ** Customer Responsibility - If the customer is responsible, they must pay for a fraction of the move.
   ** Driver Responsibility - The driver must be paid for a fraction of the move.
   *
   ** Cost to HopDrive - Always 0.
   ** Charge to Customer - Fraction of provided customer price auto-determined by inputs.
   ** Pay to Driver - Fraction of provided driver pay auto-determined by inputs.
   *
   * @param {number} options.moveId - ID of the move the accessorial is for
   * @param {boolean} options.customerResponsible - Determines if the customer should be charged
   * @param {string} options.moveStatus - Status of the related move
   * @param {number} options.movePrice - Customer price or estimated price of the move
   * @param {number} options.movePay - Drier pay or estimated pay of the move
   * @param {number} options.cancellationFee - Cancellation fee to be charged to the customer
   * @param {string} options.notes - (Optional) Additional notes on the accessorial (Customer & Driver facing)
   * @returns {object} - An insertable accessorial object
   */
  buildPartialAccessorial: (
    options = {
      moveId: null,
      customerResponsible: true,
      moveStatus: null,
      movePay: 0,
      cancellationFee: 0,
      notes: null,
    }
  ) => {
    try {
      // Validation for required props
      if (!options || typeof options !== `object`)
        throw new Error(`Failed to build partial accessorial: Invalid options prop!`);
      if (!options.moveId || typeof options.moveId !== `number`)
        throw new Error(`Failed to build partial accessorial: Invalid moveId prop!`);
      if (typeof options.customerResponsible !== `boolean`)
        throw new Error(`Failed to build partial accessorial: Invalid customerResponsible prop!`);
      if (typeof options.moveStatus !== `string`)
        throw new Error(`Failed to build partial accessorial: Invalid moveStatus prop!`);
      if (typeof options.movePay !== `number`)
        throw new Error(`Failed to build partial accessorial: Invalid movePay prop!`);
      if (typeof options.cancellationFee !== `number`)
        throw new Error(`Failed to build partial accessorial: Invalid cancellationFee prop!`);

      // Validation for optional props
      if (options.notes && typeof options.notes !== `string`)
        throw new Error(`Failed to build partial accessorial: Invalid notes prop!`);

      // Calculate partial price to customer (if customer is responsible)
      const partialPrice = options.customerResponsible ? options.cancellationFee : 0;

      // Calculate partial pay to driver
      const partialPay = _parent.accessorials.calcPartialPay(options.moveStatus, options.movePay);

      // Build accessorial
      const newAccessorial = _parent.accessorials.buildAccessorial({
        moveId: options.moveId,
        code: _parent.accessorials.types.PARTIAL.code,
        cost: 0,
        ar_amount: partialPrice,
        ap_amount: partialPay,
        notes: options.notes || _parent.accessorials.types.PARTIAL.desc,
      });

      // Return accessorial
      return newAccessorial;
    } catch (err) {
      console.error(err);
      return null;
    }
  },

  /** Build Ride Accessorial
   *
   ** HopDrive Responsibility - There is never any direct cost to HopDrive (Already accounted for in move cost).
   ** Customer Responsibility - If the customer is responsible, they must cover the cost of the ride + driver idle time.
   ** Driver Responsibility - If there is driver idle time, the driver must be paid for their idle time.
   *
   ** Cost to HopDrive - Always 0.
   ** Charge to Customer - Cost of the ride + Driver idle time, multiplied by the provided customer price rate.
   ** Pay to Driver - Driver idle time, multiplied by the provided driver pay rate.
   *
   * @param {number} options.moveId - ID of the move the accessorial is for
   * @param {boolean} options.customerResponsible - Determines if the customer should be charged
   * @param {number} options.rideCost - Cost of the ride
   * @param {number} options.driverIdleTimeMinutes - (Optional) Amount of time the driver was idle (in minutes)
   * @param {number} options.customerPricePerMinute - (Optional) Price per minute rate to determine customer price
   * @param {number} options.driverPayPerMinute - (Optional) Pay per minute rate to determine driver pay
   * @param {string} options.notes - (Optional) Additional notes on the accessorial (Customer & Driver facing)
   * @returns {object} - An insertable accessorial object
   */
  buildRideAccessorial: (
    options = {
      moveId: null,
      customerResponsible: true,
      rideCost: 0,
      driverIdleTimeMinutes: 0,
      customerPricePerMinute: 0,
      driverPayPerMinute: 0,
      notes: null,
    }
  ) => {
    try {
      // Validation for required props
      if (!options || typeof options !== `object`)
        throw new Error(`Failed to build ride accessorial: Invalid options prop!`);
      if (!options.moveId || typeof options.moveId !== `number`)
        throw new Error(`Failed to build ride accessorial: Invalid moveId prop!`);
      if (typeof options.customerResponsible !== `boolean`)
        throw new Error(`Failed to build ride accessorial: Invalid customerResponsible prop!`);
      if (typeof options.rideCost !== `number`)
        throw new Error(`Failed to build ride accessorial: Invalid rideCost prop!`);

      // Validation for optional props
      if (options.driverIdleTimeMinutes && typeof options.driverIdleTimeMinutes !== `number`)
        throw new Error(`Failed to build ride accessorial: Invalid driverIdleTimeMinutes prop!`);
      if (options.customerPricePerMinute && typeof options.customerPricePerMinute !== `number`)
        throw new Error(`Failed to build ride accessorial: Invalid customerPricePerMinute prop!`);
      if (options.driverPayPerMinute && typeof options.driverPayPerMinute !== `number`)
        throw new Error(`Failed to build ride accessorial: Invalid driverPayPerMinute prop!`);
      if (options.notes && typeof options.notes !== `string`)
        throw new Error(`Failed to build ride accessorial: Invalid notes prop!`);

      // Calculate ride & delay price to customer (if customer is responsible)
      const ridePrice = options.customerResponsible ? options.rideCost : 0;
      const delayPrice = options.customerResponsible
        ? _parent.accessorials.calcDelayPrice(options.driverIdleTimeMinutes, options.customerPricePerMinute)
        : 0;

      // Calculate delay pay to driver
      const delayPay = _parent.accessorials.calcDelayPay(options.driverIdleTimeMinutes, options.driverPayPerMinute);

      // Build accessorial
      const newAccessorial = _parent.accessorials.buildAccessorial({
        moveId: options.moveId,
        code: _parent.accessorials.types.RIDE.code,
        cost: 0,
        ar_amount: ridePrice + delayPrice,
        ap_amount: delayPay,
        notes: options.notes || _parent.accessorials.types.RIDE.desc,
      });

      // Return accessorial
      return newAccessorial;
    } catch (err) {
      console.error(err);
      return null;
    }
  },

  handleTriangleRideAccessorials: async (driveMoveId = 0, rideId = 0, rideCost = 0, rideshareId = 0) => {
    if (!driveMoveId || !rideId || !rideCost) {
      return { success: false, msg: 'Invalid data provided to handle accessorials.' };
    }
    let rideAccessorials;

    const GET_RIDE_ACCESSORIALS = gql`
      query getRideAccessorial($moveId: bigint!) {
        accessorials(where: { move_id: { _eq: $moveId }, code: { _eq: "ride" } }) {
          id
          notes
        }
      }
    `;

    const INSERT_TRIANGLE_ACCESSORIAL = gql`
      mutation insertAccessorial(
        $move_id: bigint!
        $code: String!
        $notes: String!
        $cost: numeric!
        $ap_amount: numeric!
        $ar_amount: numeric!
      ) {
        insert_accessorials(
          objects: {
            move_id: $move_id
            code: $code
            notes: $notes
            cost: $cost
            ap_amount: $ap_amount
            ar_amount: $ar_amount
          }
        ) {
          returning {
            id
          }
        }
      }
    `;

    const rideAccessorialsRes = await _parent.gql.query(GET_RIDE_ACCESSORIALS, { moveId: driveMoveId });
    if (rideAccessorialsRes && rideAccessorialsRes.data && Array.isArray(rideAccessorialsRes.data)) {
      rideAccessorials = rideAccessorialsRes.data;
    }

    if (!rideAccessorials) {
      return {
        success: false,
        msg: 'GQL error querying existing accessorials- cannot determine wether new accessorial needs to be added or old accessorial updated.',
      };
    }

    let variables = {
      move_id: driveMoveId,
      code: `ride`,
      notes: `Triangle move ride cost for move id: ${rideId} and  rideshare id: ${rideshareId}`,
      cost: rideCost,
      ar_amount: rideCost,
      ap_amount: 0,
    };

    try {
      if (rideAccessorials.length > 1) {
        return { success: false, msg: `Move already has two ride accessorials.` };
      } else {
        const insertRes = await _parent.gql.mutation(INSERT_TRIANGLE_ACCESSORIAL, variables);
        if (insertRes.success && insertRes.data) {
          return { success: true, msg: `Successfully inserted triangle ride accessorial for ${driveMoveId}.` };
        } else return { success: false, msg: `Failed to insert triangle ride accessorial for ${driveMoveId}.` };
      }
    } catch (err) {
      console.log('Error upserting accessorial:', err);
      return { success: false, msg: `Error upserting accessorial: ${err.message}` };
    }
  },

  /** Post/Distribute Ride Accessorials
   * In the event of grouped rideshares, the ride cost should be split evenly among the moves each driver in the group is working
   * @param {string} rideshareId - rideshare id to get the grouped drives for
   * @returns {boolean} - Success or failure of the operation
   */

  applyRideAccessorials: async (rideshareId = null) => {
    // Validation for required props
    if (!rideshareId) {
      return { success: false, msg: 'No rideshare ID provided. Could not create shared ride group accessorials.' };
    }

    //If this was a shared ride, divvy up the ride accessorials among the group of moves
    //Otherwise, apply the full ride accessorial to the single move
    try {
      const driveMovesRes = await _parent.ridehail.getRidegroupDrives(rideshareId);
      if (!driveMovesRes) {
        console.log('Failed to get ride info to post accessorial');
        return { success: false, msg: 'Failed to get ride info to post accessorial' };
      } else if (driveMovesRes && (!driveMovesRes.groupedDrives || !driveMovesRes.groupedDrives.length)) {
        if (driveMovesRes.parentDrive && driveMovesRes.parentDrive.isTriangleMove) {
          const triangleRes = await _parent.accessorials.handleTriangleRideAccessorials(
            driveMovesRes.parentDriveId,
            driveMovesRes.parentDrive.rideId,
            driveMovesRes.totalRideCost,
            rideshareId
          );
          return triangleRes;
        } else {
          try {
            const newAccessorial = _parent.accessorials.buildAccessorial({
              moveId: driveMovesRes.parentDriveId,
              code: _parent.accessorials.types.RIDE.code,
              cost: driveMovesRes.totalRideCost,
              ar_amount: 0,
              ap_amount: 0,
              notes: `The cost of a rideshare for a single driver.`,
            });

            //Insert the accessorial
            const res = await _parent.accessorials.create([newAccessorial]);
            if (res && res.data && res.data.affected_rows > 0) {
              return { success: true, msg: `Ride accessorial applied to move ${driveMovesRes.parentDriveId}.` };
            } else
              return {
                success: false,
                msg: `Failed to apply ride accessorial to move ${driveMovesRes.parentDriveId}. `,
              };
          } catch (err) {
            console.error('error posting ride accessorial to single move', err);
            return { success: false, msg: err.toString() };
          }
        }
      } else {
        //Loop through the grouped drives and apply the ride accessorial in the amount of the returned ride cost per drive
        let accessorialsAdded = 0;
        for (let i = 0; i < driveMovesRes.groupedDrives.length; i++) {
          if (driveMovesRes.groupedDrives[i].cost <= 0) continue;
          const newAccessorial = _parent.accessorials.buildAccessorial({
            moveId: driveMovesRes.groupedDrives[i].driveMoveId,
            code: _parent.accessorials.types.RIDE.code,
            cost: driveMovesRes.groupedDrives[i].cost,
            ar_amount: 0,
            ap_amount: 0,
            notes: `The cost of the rideshare as split among grouped drivers.`,
          });

          //Insert the accessorial
          const res = await _parent.accessorials.create([newAccessorial]);
          if (res && res.data && res.data.affected_rows > 0) {
            accessorialsAdded += res.data.affected_rows;
          }
        }
        if (accessorialsAdded > 0) {
          return { success: true, msg: `Shared ride accessorials applied to ${accessorialsAdded} moves.` };
        } else {
          return { success: false, msg: `Failed to apply shared ride accessorials to any moves.` };
        }
      }
    } catch (err) {
      console.error(err);
      return { success: false, msg: err.toString() };
    }
  },

  /** Recalculates ride and surge accessorials when the composition of the ride group changes, re-distributing the ride accessorials across the remaining moves
   * @param {uuid} groupId - rideshare group id to get the grouped drives for
   * @returns {object} - Success or failure of the operation wtih message
   */
  updateRidegroupAccessorials: async (groupId = null) => {
    if (!groupId) {
      return { success: false, msg: 'No group ID provided. Could not update accessorials.' };
    }

    let associatedDrives;
    let rideCost;
    try {
      rideCost = await _parent.ridehail.getLyftRideCostForGroup(groupId);
      associatedDrives = await _parent.ridehail.getRidegroupCostsToUpdate(groupId, rideCost);
    } catch (err) {
      console.log('error', err);
      return { success: false, msg: 'Failed to get group ride info to update accessorial' };
    }

    if (!associatedDrives.length > 0) {
      console.log('No ride cost or associated drives found to update accessorial');
      return { success: false, msg: 'No ride cost or associate drives found to update accessorial' };
    } else {
      let accessorialsUpdated = 0;
      try {
        for (let i = 0; i < associatedDrives.length; i++) {
          if (!associatedDrives[i].cost || associatedDrives[i].cost <= 0 || !associatedDrives[i].rideAccessorial)
            continue;
          const newAccessorial = _parent.accessorials.buildAccessorial({
            moveId: associatedDrives[i].driveMoveId,
            code: 'ride',
            cost: associatedDrives[i].cost,
            ar_amount: 0,
            ap_amount: 0,
            notes: `The cost of a rideshare surge for a single driver.`,
          });
          const res = await _parent.accessorials.updateRideAccessorial(
            associatedDrives[i].rideAccessorial,
            newAccessorial
          );
          if (res && res.data && res.data.affected_rows > 0) {
            accessorialsUpdated += res.data.affected_rows;
          }

          if (associatedDrives[i].driveMoveId && associatedDrives[i].surgeCost) {
            const surgeAccessorial = _parent.accessorials.buildAccessorial({
              moveId: associatedDrives[i].driveMoveId,
              code: 'surge',
              cost: 0,
              ar_amount: associatedDrives[i].surgeCost,
              ap_amount: 0,
              notes: `The cost of a rideshare surge for a single driver.`,
            });
            const surgeRes = await _parent.accessorials.updateSurge(associatedDrives[i].driveMoveId, surgeAccessorial);
            if (surgeRes && surgeRes.data && surgeRes.data.affected_rows > 0) {
              accessorialsUpdated += surgeRes.data.affected_rows;
            }
          }
        }
        if (accessorialsUpdated > 0) {
          return { success: true, msg: `Shared ride accessorials updated for ${accessorialsUpdated} moves.` };
        } else {
          return { success: false, msg: `Failed to update shared ride accessorials for any moves.` };
        }
      } catch (err) {
        console.error(err);
        return { success: false, msg: err.toString() };
      }
    }
  },

  /** Remove Ridegroup-Related Accessorials
   * If a driver misses their grouped ride but wasn't removed from the group before the accessorials were posted
   * we'll need to remove the ride and surge accessorials from their move, since they record the distributed cost
   * from the ridegroup they're no longer a part of.
   * @param {bigint} moveId - ID of the drive move
   */

  removeRidegroupAccessorials: async (moveId = null) => {
    if (!moveId) {
      return { success: false, msg: 'No move ID provided. Could not remove ridegroup accessorials.' };
    }

    const GET_RIDEGROUP_ACCESSORIALS = gql`
      query sdk_accessorial_getRidegroupAccessorialsForMove($moveId: bigint!) {
        accessorials(
          where: { move_id: { _eq: $moveId }, code: { _in: ["ride", "surge"] } }
          order_by: { createdat: asc }
        ) {
          id
          code
        }
      }
    `;

    const REMOVE_ACCESSORIALS = gql`
      mutation sdk_accessorial_removeRidegroupAccessorialsForMove($ids: [bigint!]!) {
        delete_accessorials(where: { id: { _in: $ids } }) {
          affected_rows
        }
      }
    `;

    const REMOVE_APPAYMENTS = gql`
      mutation sdk_accessorial_removeRidegroupAppaymentsForMove($ids: [bigint!]!) {
        delete_appayments(where: { accessorial_id: { _in: $ids } }) {
          affected_rows
        }
      }
    `;

    try {
      //Get the ridegroup accessorials for the move
      const accessorialsRes = await _parent.gql.query(GET_RIDEGROUP_ACCESSORIALS, { moveId });
      if (!accessorialsRes || !accessorialsRes.success) {
        return { success: false, msg: 'Ridegroup accessorial query failed' };
      }

      const ridegroupAccessorials = accessorialsRes.data ? accessorialsRes.data : null;
      if (!ridegroupAccessorials || !ridegroupAccessorials.length) {
        return { success: false, msg: 'No ridegroup accessorials found to remove' };
      }

      //Build the mutation variables to remove the oldest ride and surge accessorials on the move
      const surgeAccessorials = ridegroupAccessorials.filter(accessorial => accessorial.code === 'surge');
      const surgeToRemove = surgeAccessorials.length > 0 ? surgeAccessorials[0].id : null;

      const rideAccessorials = ridegroupAccessorials.filter(accessorial => accessorial.code === 'ride');
      const rideToRemove = rideAccessorials.length > 0 ? rideAccessorials[0].id : null;

      let accessorialsToRemove = [];
      if (surgeToRemove) accessorialsToRemove.push(surgeToRemove);
      if (rideToRemove) accessorialsToRemove.push(rideToRemove);

      if (!accessorialsToRemove.length) {
        return { success: false, msg: 'No ridegroup accessorials found to remove' };
      }

      //First, we need to remove the associated AP records or else the attempt to remove the accessorials will fail on a foreign key constraint
      const appaymentsRes = await _parent.gql.mutation(REMOVE_APPAYMENTS, { ids: accessorialsToRemove });
      if (!appaymentsRes.success) {
        return { success: false, msg: 'Could not remove accessorials; associated AP record failed to delete' };
      }

      //Finally, we can delete the accessorials
      const deleteAccessorialsRes = await _parent.gql.mutation(REMOVE_ACCESSORIALS, { ids: accessorialsToRemove });
      if (deleteAccessorialsRes && deleteAccessorialsRes.success) {
        return {
          success: true,
          msg: `${res.delete_accessorials.affected_rows} Ridegroup accessorial(s) removed for move ${moveId}.`,
        };
      } else {
        return { success: false, msg: `Failed to remove ridegroup accessorials for move ${moveId}.` };
      }
    } catch (err) {
      console.error(err);
      return { success: false, msg: err.toString() };
    }
  },

  /** Post/Distribute Surge Accessorials
   * In the event of grouped rideshares, the surge cost should be split evenly among the moves each driver in the group is working
   * @param {string} rideshareId - rideshare id to get the grouped drives for
   * @param {number} surgeCost - cost of the surge
   * @returns {object} - Success or failure of the operation wtih message
   */

  applySurgeAccessorials: async (rideshareId = null, surgeCost = null, update = false) => {
    // Validation for required props
    if (!rideshareId) {
      return { success: false, msg: 'No rideshare ID provided. Could not create surge accessorials.' };
    }

    if (!surgeCost) {
      return { success: false, msg: 'No surge cost provided. Could not create surge accessorials.' };
    }

    //If this was a shared ride, divvy up the surge accessorials among the group of moves
    //Otherwise, apply the full surge accessorial to the single move
    try {
      const driveMovesRes = await _parent.ridehail.getRidegroupDrives(rideshareId, surgeCost);
      console.log('grouped drives', driveMovesRes.groupedDrives);
      if (!driveMovesRes) {
        console.log('Failed to get ride info to post accessorial');
        return { success: false, msg: 'Failed to get ride info to post accessorial' };
      } else if (driveMovesRes && driveMovesRes.groupedDrives && !driveMovesRes.groupedDrives.length) {
        try {
          const newAccessorial = _parent.accessorials.buildAccessorial({
            moveId: driveMovesRes.parentDriveId,
            code: _parent.accessorials.types.SURGE.code,
            cost: surgeCost,
            ar_amount: surgeCost,
            ap_amount: 0,
            notes: `The cost of a rideshare surge for a single driver.`,
          });

          //Update existing surge accessorials if present
          if (update) {
            const res = await _parent.accessorials.updateSurge(driveMovesRes.parentDriveId, newAccessorial);
            if (res && res.data && res.data.affected_rows > 0) {
              return { success: true, msg: `Surge accessorial updated for move ${driveMovesRes.parentDriveId}.` };
            } else
              return {
                success: false,
                msg: `Failed to apply surge accessorial to move ${driveMovesRes.parentDriveId}. `,
              };
          } else {
            //Insert the accessorial
            const res = await _parent.accessorials.create([newAccessorial]);
            if (res && res.data && res.data.affected_rows > 0) {
              return { success: true, msg: `Surge accessorial applied to move ${driveMovesRes.parentDriveId}.` };
            } else
              return {
                success: false,
                msg: `Failed to apply surge accessorial to move ${driveMovesRes.parentDriveId}. `,
              };
          }
        } catch (err) {
          return { success: false, msg: err.toString() };
        }
      } else {
        //Loop through the grouped drives and apply the ride accessorial in the amount of the returned ride cost per drive
        let accessorialsAdded = 0;
        for (let i = 0; i < driveMovesRes.groupedDrives.length; i++) {
          const newAccessorial = _parent.accessorials.buildAccessorial({
            moveId: driveMovesRes.groupedDrives[i].driveMoveId,
            code: _parent.accessorials.types.SURGE.code,
            cost: 0,
            ar_amount: driveMovesRes.groupedDrives[i].cost,
            ap_amount: 0,
            notes: `The cost of the rideshare surge as split among grouped drivers.`,
          });

          if (update) {
            const res = await _parent.accessorials.updateSurge(
              driveMovesRes.groupedDrives[i].driveMoveId,
              newAccessorial
            );
            if (res && res.data && res.data.affected_rows > 0) {
              accessorialsAdded += res.data.affected_rows;
            }
          } else {
            const res = await _parent.accessorials.create([newAccessorial]);
            if (res && res.data && res.data.affected_rows > 0) {
              accessorialsAdded += res.data.affected_rows;
            }
          }
        }
        if (accessorialsAdded > 0) {
          return { success: true, msg: `Shared ride accessorials applied to ${accessorialsAdded} moves.` };
        } else {
          return { success: false, msg: `Failed to apply shared ride accessorials to any moves.` };
        }
      }
    } catch (err) {
      console.error(err);
      return { success: false, msg: err.toString() };
    }
  },

  redistributeSurgeAccessorial: async (surgeCost = null, groupedDrives = []) => {
    if (!surgeCost) {
      return { success: false, msg: 'No surge cost provided. Could not create surge accessorials.' };
    }

    //If this was a shared ride, divvy up the surge accessorials among the group of moves
    //Otherwise, apply the full surge accessorial to the single move
    try {
      const driveMovesRes = await _parent.ridehail.getRidegroupDrives(rideshareId, surgeCost);
      console.log('grouped drives', driveMovesRes.groupedDrives);
      if (!driveMovesRes) {
        console.log('Failed to get ride info to post accessorial');
        return { success: false, msg: 'Failed to get ride info to post accessorial' };
      } else if (driveMovesRes && driveMovesRes.groupedDrives && !driveMovesRes.groupedDrives.length) {
        try {
          const newAccessorial = _parent.accessorials.buildAccessorial({
            moveId: driveMovesRes.parentDriveId,
            code: _parent.accessorials.types.SURGE.code,
            cost: surgeCost,
            ar_amount: surgeCost,
            ap_amount: 0,
            notes: `The cost of a rideshare surge for a single driver.`,
          });

          //Update existing surge accessorials if present
          if (update) {
            const res = await _parent.accessorials.updateSurge(driveMovesRes.parentDriveId, newAccessorial);
            if (res && res.data && res.data.affected_rows > 0) {
              return { success: true, msg: `Surge accessorial updated for move ${driveMovesRes.parentDriveId}.` };
            } else
              return {
                success: false,
                msg: `Failed to apply surge accessorial to move ${driveMovesRes.parentDriveId}. `,
              };
          } else {
            //Insert the accessorial
            const res = await _parent.accessorials.create([newAccessorial]);
            if (res && res.data && res.data.affected_rows > 0) {
              return { success: true, msg: `Surge accessorial applied to move ${driveMovesRes.parentDriveId}.` };
            } else
              return {
                success: false,
                msg: `Failed to apply surge accessorial to move ${driveMovesRes.parentDriveId}. `,
              };
          }
        } catch (err) {
          return { success: false, msg: err.toString() };
        }
      } else {
        //Loop through the grouped drives and apply the ride accessorial in the amount of the returned ride cost per drive
        let accessorialsAdded = 0;
        for (let i = 0; i < driveMovesRes.groupedDrives.length; i++) {
          const newAccessorial = _parent.accessorials.buildAccessorial({
            moveId: driveMovesRes.groupedDrives[i].driveMoveId,
            code: _parent.accessorials.types.SURGE.code,
            cost: 0,
            ar_amount: driveMovesRes.groupedDrives[i].cost,
            ap_amount: 0,
            notes: `The cost of the rideshare surge as split among grouped drivers.`,
          });

          if (update) {
            const res = await _parent.accessorials.updateSurge(
              driveMovesRes.groupedDrives[i].driveMoveId,
              newAccessorial
            );
            if (res && res.data && res.data.affected_rows > 0) {
              accessorialsAdded += res.data.affected_rows;
            }
          } else {
            const res = await _parent.accessorials.create([newAccessorial]);
            if (res && res.data && res.data.affected_rows > 0) {
              accessorialsAdded += res.data.affected_rows;
            }
          }
        }
        if (accessorialsAdded > 0) {
          return { success: true, msg: `Shared ride accessorials applied to ${accessorialsAdded} moves.` };
        } else {
          return { success: false, msg: `Failed to apply shared ride accessorials to any moves.` };
        }
      }
    } catch (err) {
      console.error(err);
      return { success: false, msg: err.toString() };
    }
  },

  /** Build Roadside Accessorial
   *
   ** HopDrive Responsibility - HopDrive must cover the raw roadside assistance cost.
   ** Customer Responsibility - If the customer is responsible, they must cover the cost of the roadside assistance + driver idle time.
   ** Driver Responsibility - If there is driver idle time, the driver must be paid for their idle time.
   *
   ** Cost to HopDrive - Cost of the roadside assistance.
   ** Charge to Customer - Cost of the roadside assistance + Driver idle time, multiplied by the provided customer price rate.
   ** Pay to Driver - Driver idle time, multiplied by the provided driver pay rate.
   *
   * @param {number} options.moveId - ID of the move the accessorial is for
   * @param {boolean} options.customerResponsible - Determines if the customer should be charged
   * @param {number} options.roadsideCost - Raw cost of the roadside assistance
   * @param {number} options.driverIdleTimeMinutes - (Optional) Amount of time the driver was idle (in minutes)
   * @param {number} options.customerPricePerMinute - (Optional) Price per minute rate to determine customer price
   * @param {number} options.driverPayPerMinute - (Optional) Pay per minute rate to determine driver pay
   * @param {string} options.notes - (Optional) Additional notes on the accessorial (Customer & Driver facing)
   * @returns {object} - An insertable accessorial object
   */
  buildRoadsideAccessorial: (
    options = {
      moveId: null,
      customerResponsible: true,
      roadsideCost: 0,
      driverIdleTimeMinutes: 0,
      customerPricePerMinute: 0,
      driverPayPerMinute: 0,
      notes: null,
    }
  ) => {
    try {
      // Validation for required props
      if (!options || typeof options !== `object`)
        throw new Error(`Failed to build roadside accessorial: Invalid options prop!`);
      if (!options.moveId || typeof options.moveId !== `number`)
        throw new Error(`Failed to build roadside accessorial: Invalid moveId prop!`);
      if (typeof options.customerResponsible !== `boolean`)
        throw new Error(`Failed to build roadside accessorial: Invalid customerResponsible prop!`);
      if (typeof options.roadsideCost !== `number`)
        throw new Error(`Failed to build roadside accessorial: Invalid roadsideCost prop!`);

      // Validation for optional props
      if (options.driverIdleTimeMinutes && typeof options.driverIdleTimeMinutes !== `number`)
        throw new Error(`Failed to build roadside accessorial: Invalid driverIdleTimeMinutes prop!`);
      if (options.customerPricePerMinute && typeof options.customerPricePerMinute !== `number`)
        throw new Error(`Failed to build roadside accessorial: Invalid customerPricePerMinute prop!`);
      if (options.driverPayPerMinute && typeof options.driverPayPerMinute !== `number`)
        throw new Error(`Failed to build roadside accessorial: Invalid driverPayPerMinute prop!`);
      if (options.notes && typeof options.notes !== `string`)
        throw new Error(`Failed to build roadside accessorial: Invalid notes prop!`);

      // Calculate roadside & delay price to customer (if customer is responsible)
      const roadsidePrice = options.customerResponsible ? options.roadsideCost : 0;
      const delayPrice = options.customerResponsible
        ? _parent.accessorials.calcDelayPrice(options.driverIdleTimeMinutes, options.customerPricePerMinute)
        : 0;

      // Calculate delay pay to driver
      const delayPay = _parent.accessorials.calcDelayPay(options.driverIdleTimeMinutes, options.driverPayPerMinute);

      // Build accessorial
      const newAccessorial = _parent.accessorials.buildAccessorial({
        moveId: options.moveId,
        code: _parent.accessorials.types.ROADSIDE.code,
        cost: options.roadsideCost,
        ar_amount: roadsidePrice + delayPrice,
        ap_amount: delayPay,
        notes: options.notes || _parent.accessorials.types.ROADSIDE.desc,
      });

      // Return accessorial
      return newAccessorial;
    } catch (err) {
      console.error(err);
      return null;
    }
  },

  /** Build Tow Accessorial
   *
   ** HopDrive Responsibility - HopDrive must cover the raw tow cost.
   ** Customer Responsibility - If the customer is responsible, they must cover the cost of the tow + driver idle time.
   ** Driver Responsibility - If there is driver idle time, the driver must be paid for their idle time.
   *
   ** Cost to HopDrive - Cost of the tow.
   ** Charge to Customer - Cost of the tow + Driver idle time, multiplied by the provided customer price rate.
   ** Pay to Driver - Driver idle time, multiplied by the provided driver pay rate.
   *
   * @param {number} options.moveId - ID of the move the accessorial is for
   * @param {boolean} options.customerResponsible - Determines if the customer should be charged
   * @param {number} options.towCost - Raw cost of the tow
   * @param {number} options.driverIdleTimeMinutes - (Optional) Amount of time the driver was idle (in minutes)
   * @param {number} options.customerPricePerMinute - (Optional) Price per minute rate to determine customer price
   * @param {number} options.driverPayPerMinute - (Optional) Pay per minute rate to determine driver pay
   * @param {string} options.notes - (Optional) Additional notes on the accessorial (Customer & Driver facing)
   * @returns {object} - An insertable accessorial object
   */
  buildTowAccessorial: (
    options = {
      moveId: null,
      customerResponsible: true,
      towCost: 0,
      driverIdleTimeMinutes: 0,
      customerPricePerMinute: 0,
      driverPayPerMinute: 0,
      notes: null,
    }
  ) => {
    try {
      // Validation for required props
      if (!options || typeof options !== `object`)
        throw new Error(`Failed to build tow accessorial: Invalid options prop!`);
      if (!options.moveId || typeof options.moveId !== `number`)
        throw new Error(`Failed to build tow accessorial: Invalid moveId prop!`);
      if (typeof options.customerResponsible !== `boolean`)
        throw new Error(`Failed to build tow accessorial: Invalid customerResponsible prop!`);
      if (typeof options.towCost !== `number`)
        throw new Error(`Failed to build tow accessorial: Invalid towCost prop!`);

      // Validation for optional props
      if (options.driverIdleTimeMinutes && typeof options.driverIdleTimeMinutes !== `number`)
        throw new Error(`Failed to build tow accessorial: Invalid driverIdleTimeMinutes prop!`);
      if (options.customerPricePerMinute && typeof options.customerPricePerMinute !== `number`)
        throw new Error(`Failed to build tow accessorial: Invalid customerPricePerMinute prop!`);
      if (options.driverPayPerMinute && typeof options.driverPayPerMinute !== `number`)
        throw new Error(`Failed to build tow accessorial: Invalid driverPayPerMinute prop!`);
      if (options.notes && typeof options.notes !== `string`)
        throw new Error(`Failed to build tow accessorial: Invalid notes prop!`);

      // Calculate tow & delay price to customer (if customer is responsible)
      const towPrice = options.customerResponsible ? options.towCost : 0;
      const delayPrice = options.customerResponsible
        ? _parent.accessorials.calcDelayPrice(options.driverIdleTimeMinutes, options.customerPricePerMinute)
        : 0;

      // Calculate delay pay to driver
      const delayPay = _parent.accessorials.calcDelayPay(options.driverIdleTimeMinutes, options.driverPayPerMinute);

      // Build accessorial
      const newAccessorial = _parent.accessorials.buildAccessorial({
        moveId: options.moveId,
        code: _parent.accessorials.types.TOW.code,
        cost: options.towCost,
        ar_amount: towPrice + delayPrice,
        ap_amount: delayPay,
        notes: options.notes || _parent.accessorials.types.TOW.desc,
      });

      // Return accessorial
      return newAccessorial;
    } catch (err) {
      console.error(err);
      return null;
    }
  },
};

module.exports = accessorials;
